import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchStores } from './store/monthlyReportsSlice';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import CustomersDropDown from '../../Loyalty/CustomersDropdown';
import StoresDropDown from '../../Analytics/StoresDropDown';
import CustomSelector from '../../../commonComponents/Selector';
import {
  Container,
  HeaderSection,
  HeaderLeft,
  HeaderRight,
  TitleSection,
  MainTitle,
  Subtitle,
  BackButton,
  FilterSection,
  FilterLabel,
} from './Styles';
import MonthlyReportSummary from './MonthlyReportSummary';
import WeeklyBreakdownSection from './WeeklyBreakdownSection';
import MonthlyInsightsSection from './MonthlyInsightsSection';
import { Merchant, MonthOption } from './types';
import { MONTHS_TO_GENERATE } from './constants';

const MonthlyReports: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { reportData, loading, storesLoading, stores } = useSelector(
    (state: RootState) => state.monthlyReports
  );

  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<moment.Moment>(moment());
  const [storeId, setStoreId] = useState<string>('');

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const userRole = useMemo(() => user?.role, [user?.role]);

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };
  const antdTheme = getAntdThemeConfig(isDarkMode);

  useEffect(() => {
    if (userRole === 'merchant' && !merchant) {
      setMerchant(user);
    }
  }, [userRole, merchant, user]);

  useEffect(() => {
    if (userRole === 'admin' || storeId || storesLoading) {
      return;
    }
    
    if (stores.length === 0) {
      dispatch(fetchStores());
    }
  }, [userRole, storeId, storesLoading, stores.length, dispatch]);

  useEffect(() => {
    if (userRole !== 'admin' && stores.length > 0 && !storeId) {
      setStoreId(stores[0].id);
    }
  }, [stores, storeId, userRole]);

  const generateMonthOptions = useCallback((): MonthOption[] => {
    const options: MonthOption[] = [];
    for (let i = 0; i < MONTHS_TO_GENERATE; i++) {
      const month = moment().subtract(i, 'months');
      options.push({
        value: month.format('YYYY-MM'),
        label: month.format('MMMM YYYY'),
      });
    }
    return options;
  }, []);

  const monthOptions = useMemo(() => generateMonthOptions(), [generateMonthOptions]);

  const handleMonthSelection = useCallback((value: string | number) => {
    setSelectedMonth(moment(String(value)));
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={antdTheme}>
        <Container>
          <HeaderSection>
            <HeaderLeft>
              <BackButton
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                aria-label="Go Back to Reports Dashboard"
              />
              <TitleSection>
                <MainTitle level={2}>Monthly Reports</MainTitle>
                <Subtitle>
                  View detailed monthly sales and performance reports
                </Subtitle>
              </TitleSection>
            </HeaderLeft>
            <HeaderRight>
              <FilterSection size="middle" wrap>
                {userRole === 'admin' && (
                  <Space>
                    <FilterLabel>By Merchants</FilterLabel>
                    <CustomersDropDown
                      onOptionSelect={(merch: Merchant) => {
                        setMerchant(merch);
                      }}
                    />
                  </Space>
                )}
                <Space>
                  <FilterLabel>By Store</FilterLabel>
                  <StoresDropDown
                    onOptionSelect={(store: { id: string }) => {
                      setStoreId(store.id);
                    }}
                    merchantId={merchant?.id}
                    role={userRole}
                  />
                </Space>
                <Space>
                  <FilterLabel>Month</FilterLabel>
                  <CustomSelector
                    value={moment(selectedMonth).format('YYYY-MM')}
                    onChange={handleMonthSelection}
                    options={monthOptions}
                    width={180}
                    height={32}
                    borderRadius={8}
                  />
                </Space>
              </FilterSection>
            </HeaderRight>
          </HeaderSection>

          <MonthlyReportSummary
            merchant={merchant}
            storeId={storeId}
            selectedMonth={selectedMonth}
          />
          <MonthlyInsightsSection reportData={reportData} loading={loading} />
          <WeeklyBreakdownSection reportData={reportData} loading={loading} />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MonthlyReports;
